// Per-line subscription plan selector for the cart (drawer + page).
// A <details> dropdown of PDP-style selector-card radios. On selection it applies/removes the
// selling plan via /cart/change.js, then dispatches CartLinesUpdateEvent so the theme's
// cart-items-component morphs both cart sections (same refresh path as build-a-box.js).
// Uses `line` (not id), per the change.js requirement that a selling_plan is addressed by line.

import { CartLinesUpdateEvent } from '@shopify/events';

class CartPlanSelector extends HTMLElement {
  connectedCallback() {
    this.details = this.querySelector('details');
    this._onChange = this._onChange.bind(this);
    this._onDocClick = this._onDocClick.bind(this);
    this.addEventListener('change', this._onChange);
    document.addEventListener('click', this._onDocClick);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this._onChange);
    document.removeEventListener('click', this._onDocClick);
  }

  // Close the dropdown when clicking elsewhere.
  _onDocClick(event) {
    if (this.details?.open && !this.contains(event.target)) this.details.open = false;
  }

  // Collect every cart section id on the page so both drawer and page re-render.
  #sectionIds() {
    const ids = [];
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) ids.push(el.dataset.sectionId);
    });
    return ids;
  }

  async _onChange(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;

    const line = Number(this.dataset.line);
    const quantity = Number(this.dataset.quantity) || 1;
    if (!line) return;

    const selling_plan = input.value === '' ? null : Number(input.value);

    if (this.details) this.details.open = false;
    this.dataset.pending = 'true';

    const body = JSON.stringify({
      line,
      quantity,
      selling_plan,
      sections: this.#sectionIds().join(','),
      sections_url: window.location.pathname,
    });

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'update',
        context: 'cart',
        lines: [{ id: this.dataset.key, quantity }],
        promise: deferred.promise,
      })
    );

    try {
      const res = await fetch(Theme.routes.cart_change_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body,
      });
      const data = await res.json();
      if (data.status && data.status !== 200) {
        deferred.reject(new Error(data.message || 'cart/change failed'));
        delete this.dataset.pending;
        return;
      }
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(data),
        detail: { items: data.items, source: 'cart-plan-selector', sections: data.sections, didError: false },
      });
    } catch (err) {
      deferred.reject(err);
      delete this.dataset.pending;
      console.error('[cart-plan-selector] change failed', err);
    }
  }
}

if (!customElements.get('cart-plan-selector')) {
  customElements.define('cart-plan-selector', CartPlanSelector);
}
